import axios from "axios";
import Keycloak from "keycloak-js/dist/keycloak.js";

import Config from "./Config";
import Version from "./Version";
import Emitter from "./Emitter";
import Settings from "./SettingsService";
import { PERMISSION_TYPE } from "./UsersPermission";

axios.interceptors.response.use(
    function (successRes) {
        return successRes;
    },
    function (error) {
        if (typeof error.response !== "undefined") {
            let code = error.response.status,
                data = error.response.data;
            if (code === 403) {
                Emitter.emit("REAUTH");
            } else if (code === 503 && data.indexOf("maintenance") === 0) {
                const until = data.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/);
                if (until !== null) {
                    window.localStorage.setItem("MAINTENANCE_UNTIL", until[0]);
                }
                Emitter.emit("MAINTENANCE");
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Should assist User Authentaticon
 * @hideconstructor
 */
class AuthService {
    static #ssoRedirectPath = "sso-redirect";
    static #localTokenKey = "api-token";
    static #localUserKey = "local-user";
    static #localLegacyKey = "jwt";
    /**
     * #localLegacyKey is used for keeping retrocompatibility with the old JWT token used by
     * the Attribute Manager chrome extension
     */
    static #keycloak = null;
    static #redirect2;
    static #authenticating = true;
    /**
     * Should register the location through which the user has landed in the system.
     * Someone trying to access https://tools.trackstreet.com/something would be "init"iated with
     * /something
     * @param {string} redirect2
     * @memberof AuthService
     */
    static init() {
        if (AuthService.#keycloak !== null) {
            return;
        }

        AuthService.#keycloak = new Keycloak({
            url: Config.KEYCLOAK_URL,
            realm: Config.KEYCLOAK_REALM,
            clientId: Config.KEYCLOAK_CLIENT_ID,
        });

        const { location } = window;
        AuthService.#redirect2 =
            localStorage.getItem("redirect2") ??
            location.pathname + location.hash;
        if (
            AuthService.#redirect2.indexOf(AuthService.#ssoRedirectPath) === -1
        ) {
            localStorage.setItem("redirect2", AuthService.#redirect2);
        }

        if (Version.Notes !== Settings.Get("LastRelease")) {
            AuthService.#redirect2 = "/releases";
        }

        const tokens = JSON.parse(
            localStorage.getItem(AuthService.#localTokenKey)
        );
        AuthService.#keycloak
            .init({
                onLoad: "login-required",
                redirectUri: `${Config.APP_URL}/${
                    AuthService.#ssoRedirectPath
                }`,
                enableLogging: true,
                checkLoginIframe: false,
                useNonce: false,
                token: tokens?.access_token,
                refreshToken: tokens?.refresh_token,
            })
            .then(async function () {
                const tokens = {
                    access_token: AuthService.#keycloak.token,
                    refresh_token: AuthService.#keycloak.refreshToken,
                };

                const userDetails = await AuthService.validateLogin(
                    tokens.access_token
                );

                if (userDetails === null) {
                    AuthService.clearTokensAndUsers();
                    AuthService.reauth();
                    return;
                }

                axios.defaults.headers.common["Authorization"] =
                    tokens.access_token;
                axios.interceptors.request.use(AuthService.refreshToken);

                localStorage.setItem(
                    AuthService.#localTokenKey,
                    JSON.stringify(tokens)
                );
                localStorage.setItem(
                    AuthService.#localLegacyKey,
                    tokens.access_token
                );

                // Retrieves the user info (name & picture) from the JWT token
                const keycloakUser = AuthService.#keycloak.idTokenParsed;
                keycloakUser.picture = userDetails.profile_image
                    ? userDetails.profile_image
                    : null;
                keycloakUser.permissions = [...userDetails.permissions];
                localStorage.setItem(
                    AuthService.#localUserKey,
                    JSON.stringify(keycloakUser)
                );
                Emitter.emit("LOGGED_IN");
                AuthService.#authenticating = false;
            })
            .catch(function (e) {
                console.error(e);
            });
    }

    static reauth() {
        const { location } = window;
        localStorage.setItem("redirect2", location.pathname + location.hash);
        AuthService.#keycloak.login();
    }

    /**
     * Checks whether the current token is still valid.
     * @returns {boolean}
     */
    static ping() {
        return axios
            .get(Config.API_URL + "ping")
            .then((response) => {
                return (
                    typeof response != "undefined" &&
                    typeof response.data != "undefined" &&
                    response.data === "pong"
                );
            })
            .catch((error) => {
                return false;
            });
    }

    static async validateLogin(token) {
        try {
            const response = await axios.post(
                `${Config.API_URL}validate-login`,
                {},
                { headers: { Authorization: token } }
            );

            return response.data && response.data.status === true
                ? response.data
                : null;
        } catch (error) {
            AuthService.clearTokensAndUsers();
            AuthService.reauth();
        }
    }

    /**
     * Removes all tokens from the browser, "resetting" the session
     */
    static logout() {
        AuthService.clearTokensAndUsers();
        Emitter.emit("LOGGED_OUT");
    }

    static clearTokensAndUsers() {
        localStorage.removeItem(AuthService.#localUserKey);
        localStorage.removeItem(AuthService.#localTokenKey);
        localStorage.removeItem(AuthService.#localLegacyKey);
        axios.defaults.headers.common["Authorization"] = null;
        axios.interceptors.request.eject(AuthService.refreshToken);
    }

    static async refreshToken(config) {
        if (!AuthService.#keycloak.isTokenExpired(30)) return config;

        const refreshed = await AuthService.#keycloak.updateToken(30);
        if (!refreshed) {
            AuthService.clearTokensAndUsers();
            AuthService.reauth();
            return {
                ...config,
                signal: AbortSignal.abort(),
            };
        }

        const refreshedTokens = {
            access_token: AuthService.#keycloak.token,
            refresh_token: AuthService.#keycloak.refreshToken,
        };
        axios.defaults.headers.common["Authorization"] =
            refreshedTokens.access_token;
        config.headers.common["Authorization"] = refreshedTokens.access_token;
        localStorage.setItem(
            AuthService.#localTokenKey,
            JSON.stringify(refreshedTokens)
        );
        localStorage.setItem(
            AuthService.#localLegacyKey,
            refreshedTokens.access_token
        );

        return config;
    }

    /**
     * @returns {(false|object)} false if no user has been authenticated. Object otherwise
     */
    static user() {
        const user = localStorage.getItem(AuthService.#localUserKey);

        return user ? JSON.parse(user) : null;
    }

    static hasAccessTo = (area) => {
        const user = this.user() ?? {};
        const userPermission = user.permissions ?? [];
        return (
            userPermission.includes(PERMISSION_TYPE.ALL) ||
            userPermission.includes(area)
        );
    };

    static isAdmin = () => {
        return AuthService.hasAccessTo(PERMISSION_TYPE.ADMIN);
    };

    static hasAccessToCosts = () => {
        return AuthService.hasAccessTo(PERMISSION_TYPE.COSTS);
    };

    static redirectUrl = () => {
        return AuthService.#redirect2;
    };

    static clearRedirectUrl = () => {
        localStorage.removeItem("redirect2");
    };

    static isAuthenticating = () => {
        return AuthService.#authenticating;
    };
}

export default AuthService;
